// src/components/InstallAppWall.js
// Mur souple d'installation PWA — mobile web uniquement, après authentification.
//
// Principe (cf. décision produit 2026-07-13) :
//  - S'affiche en plein écran APRÈS login sur mobile web (jamais en PWA, jamais
//    sur desktop, jamais sur login/register/invitation/reset — ces pages ne sont
//    pas authentifiées donc naturellement exclues par la garde `active`).
//  - CTA adapté au contexte : Android natif (1 tap) / iOS Safari (instructions)
//    / webview WhatsApp-Insta-FB (rouvrir dans le navigateur).
//  - Échappatoire toujours visible : « Continuer sur le web ».
//  - Rappels espacés (immédiat → +7j → +30j → stop), jamais à chaque visite.
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import CIcon from '@coreui/icons-react'
import {
  cilShareBoxed,
  cilPlus,
  cilBrowser,
  cilBell,
  cilBolt,
  cilCloudDownload,
} from '@coreui/icons'
import {
  getInstallStrategy,
  promptNativeInstall,
  recordDismiss,
  shouldShowWall,
} from '../utils/pwaInstall'
import './InstallAppWall.css'

// Ne pas interrompre une action urgente : arrivée depuis une notification push
// ou un lien profond. On cible des marqueurs spécifiques (pas des termes
// génériques comme `token`/`action` qui apparaissent sur des écrans légitimes).
const isUrgentDeepLink = (location) => {
  if (location.hash) return true
  return /[?&](notif|notification|conversation|conversationId|invite|deeplink)=/i.test(
    location.search || '',
  )
}

const InstallAppWall = ({ active }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const [visible, setVisible] = useState(false)
  const [strategy, setStrategy] = useState('manual')
  const [copied, setCopied] = useState(false)
  const continueRef = useRef(null)

  useEffect(() => {
    if (!active) return
    if (isUrgentDeepLink(location)) return
    // Une seule évaluation par session pour ne pas resurgir à chaque navigation.
    if (sessionStorage.getItem('pwa_install_wall_seen')) return
    if (!shouldShowWall()) return

    const timer = setTimeout(() => {
      setStrategy(getInstallStrategy())
      setVisible(true)
      sessionStorage.setItem('pwa_install_wall_seen', '1')
    }, 1200)

    // Si Chrome émet le prompt natif un peu plus tard, on réévalue la stratégie.
    const onAvailable = () => setStrategy(getInstallStrategy())
    window.addEventListener('pwa-install-available', onAvailable)
    const onInstalled = () => setVisible(false)
    window.addEventListener('pwa-installed', onInstalled)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('pwa-install-available', onAvailable)
      window.removeEventListener('pwa-installed', onInstalled)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])

  // Bloquer le scroll de l'arrière-plan + focus initial pendant l'affichage.
  useEffect(() => {
    if (!visible) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    continueRef.current?.focus()
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [visible])

  const handleDismiss = () => {
    recordDismiss()
    setVisible(false)
  }

  const handleNativeInstall = async () => {
    const outcome = await promptNativeInstall()
    if (outcome === 'accepted') {
      setVisible(false)
    } else if (outcome === 'unavailable') {
      // Prompt plus disponible : bascule vers les instructions manuelles.
      setStrategy(getInstallStrategy())
    }
    // 'dismissed' : on laisse le mur ouvert, l'utilisateur peut réessayer.
  }

  const handleCopyLink = async () => {
    const url = window.location.href
    const flagCopied = () => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }

    // Clipboard API moderne (souvent indisponible/bloquée dans les webviews
    // in-app — d'où le fallback execCommand ci-dessous).
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url)
        flagCopied()
        return
      } catch {
        /* on tente le fallback legacy */
      }
    }

    // Fallback : textarea temporaire + execCommand('copy')
    try {
      const ta = document.createElement('textarea')
      ta.value = url
      ta.setAttribute('readonly', '')
      ta.style.position = 'absolute'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      if (ok) flagCopied()
    } catch {
      /* dernier recours : l'utilisateur copiera l'URL manuellement */
    }
  }

  if (!visible) return null

  const benefits = [
    { icon: cilBell, text: t('install_app.benefit_notifications') },
    { icon: cilBolt, text: t('install_app.benefit_fast') },
    { icon: cilCloudDownload, text: t('install_app.benefit_offline') },
  ]

  return (
    <div className="install-wall">
      <div
        className="install-wall__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="install-wall-title"
      >
        <div className="install-wall__icon" aria-hidden="true">
          <CIcon icon={cilCloudDownload} />
        </div>

        <h2 id="install-wall-title" className="install-wall__title">
          {t('install_app.title')}
        </h2>
        <p className="install-wall__subtitle">{t('install_app.subtitle')}</p>

        <ul className="install-wall__benefits">
          {benefits.map((b, i) => (
            <li key={i}>
              <CIcon icon={b.icon} className="install-wall__benefit-icon" />
              <span>{b.text}</span>
            </li>
          ))}
        </ul>

        {/* Bloc principal adapté au contexte */}
        {strategy === 'native' && (
          <button className="install-wall__cta" onClick={handleNativeInstall}>
            <CIcon icon={cilCloudDownload} /> {t('install_app.cta_install')}
          </button>
        )}

        {strategy === 'ios' && (
          <div className="install-wall__steps">
            <p className="install-wall__steps-intro">{t('install_app.ios_intro')}</p>
            <ol>
              <li>
                <CIcon icon={cilShareBoxed} className="install-wall__step-icon" />
                <span>{t('install_app.ios_step1')}</span>
              </li>
              <li>
                <CIcon icon={cilPlus} className="install-wall__step-icon" />
                <span>{t('install_app.ios_step2')}</span>
              </li>
            </ol>
          </div>
        )}

        {strategy === 'in_app' && (
          <div className="install-wall__steps">
            <div className="install-wall__inapp-warn">
              <CIcon icon={cilBrowser} className="install-wall__step-icon" />
              <span>{t('install_app.inapp_intro')}</span>
            </div>
            <button className="install-wall__cta secondary" onClick={handleCopyLink}>
              {copied ? t('install_app.link_copied') : t('install_app.copy_link')}
            </button>
          </div>
        )}

        {strategy === 'manual' && (
          <div className="install-wall__steps">
            <p className="install-wall__steps-intro">{t('install_app.manual_intro')}</p>
          </div>
        )}

        <button
          ref={continueRef}
          className="install-wall__continue"
          onClick={handleDismiss}
        >
          {t('install_app.continue_web')}
        </button>
      </div>
    </div>
  )
}

export default InstallAppWall
